import {
  createDocFromUint8Array,
  type ParticipantDocument,
  type ParticipantDocumentVersion,
} from '@piccolohealth/pbs-common';
import { ChakraV3, dataAttr, FancyDate, ScrollArea } from '@piccolohealth/ui';
import { inflection, P } from '@piccolohealth/util';
import React from 'react';
import { createModal } from '../../components/generic/Modal';
import { Editor } from '../../components/tiptap/Editor';
import { EditorOutlineSection } from '../../components/tiptap/EditorOutlineSection';
import { useEditor } from '../../components/tiptap/hooks/useEditor';
import type { HocuspocusState } from '../../components/tiptap/hooks/useHocuspocusProvider';
import { EditorLayout } from '../../components/tiptap/layout/EditorLayout';
import {
  convertYDocToJSONContent,
  generateHTML,
  getParticipantDocumentExtensions,
  postProcessContent,
} from '../../components/tiptap/utils';
import { UserAvatar } from '../../components/user/UserAvatar';
import {
  useParticipantDocumentVersionQuery,
  useParticipantDocumentVersionsInfiniteQuery,
} from '../../graphql/hooks/useParticipantQuery';
import { useRestoreParticipantDocumentVersionMutation } from '../../graphql/hooks/useRestoreParticipantDocumentVersionMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { scrollIntoView } from '../../utils/scrollIntoView';

interface ParticipantDocumentHistorySidebarProps {
  participantId: string;
  participantDocumentId: string;
  activeVersionId: string | null;
  setActiveVersionId: (versionId: string) => void;
}

const ParticipantDocumentHistorySidebar = (props: ParticipantDocumentHistorySidebarProps) => {
  const { participantId, participantDocumentId, activeVersionId, setActiveVersionId } = props;
  const { organization } = useAppContext();

  const endRef = React.useRef<HTMLDivElement>(null);

  const participantDocumentVersionsQuery = useParticipantDocumentVersionsInfiniteQuery({
    organizationId: organization.id,
    participantId,
    participantDocumentId,
    request: {
      pagination: {
        limit: 40,
      },
    },
  });

  const participantDocumentVersionsPagination = P.first(
    participantDocumentVersionsQuery.data?.pages ?? [],
  )?.organization?.participant?.document?.versions?.pagination;

  const participantDocumentVersions =
    participantDocumentVersionsQuery.data?.pages.flatMap(
      (page) =>
        page.organization?.participant?.document?.versions.results as ParticipantDocumentVersion[],
    ) ?? [];

  const latestVersion = P.first(participantDocumentVersions);
  const isLoading = participantDocumentVersionsQuery.isLoading;
  const hasNextPage = participantDocumentVersionsQuery.hasNextPage;
  const total = participantDocumentVersionsPagination?.total ?? 0;

  const fetchNextPage = () => {
    participantDocumentVersionsQuery.fetchNextPage();
  };

  // https://github.com/biomejs/biome/issues/630
  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to run this effect when the number of pages changes
  React.useEffect(() => {
    if (endRef.current) {
      scrollIntoView(endRef.current);
    }
  }, [participantDocumentVersionsQuery.data?.pages.length]);

  React.useEffect(() => {
    // TODO: Remove the active version null check when we make the active version id required
    if (!P.isNil(latestVersion) && !P.isNil(latestVersion.versionId)) {
      setActiveVersionId(latestVersion.versionId);
    }
  }, [latestVersion, setActiveVersionId]);

  if (!participantDocumentVersionsPagination) {
    return null;
  }

  const content = P.run(() => {
    if (P.isEmpty(participantDocumentVersions)) {
      return (
        <ChakraV3.Text fontSize='xs' fontWeight='bold' color='secondary'>
          No versions found
        </ChakraV3.Text>
      );
    }

    return (
      <>
        <ChakraV3.Text fontSize='xs' fontWeight='bold' color='secondary'>
          Versions
        </ChakraV3.Text>
        <ScrollArea flexDir='column' overflowY='auto' h='full'>
          <ChakraV3.Stack pr={4}>
            {participantDocumentVersions.map((document, index) => (
              <ChakraV3.Button
                as={ChakraV3.HStack}
                key={document.versionId}
                {...{ 'data-active': dataAttr(activeVersionId === document.versionId) }}
                onClick={() => {
                  // TODO: Remove the active version null check when we make the active version id required
                  if (!P.isNil(document.versionId)) {
                    setActiveVersionId(document.versionId);
                  }
                }}
                variant='ghost'
                color='gray.600'
                size='sm'
                justifyContent='start'
                whiteSpace='initial'
                h='auto'
                px={3}
                py={2}
                rounded='md'
                fontWeight='semibold'
              >
                <ChakraV3.HStack w='full' align='start'>
                  <ChakraV3.Stack gap={1}>
                    <ChakraV3.Text fontWeight='semibold'>Version {total - index}</ChakraV3.Text>
                    <FancyDate
                      fontSize='xs'
                      color='secondary'
                      date={document.createdAt}
                      showTooltip={false}
                      showHumanized={false}
                      direction='row'
                    />
                  </ChakraV3.Stack>
                  <ChakraV3.Spacer />
                  <ChakraV3.AvatarGroup gap={-3}>
                    {P.take(document.users, 3).map((member) => (
                      <UserAvatar
                        key={member.id}
                        name={member.name}
                        secondary={member.email}
                        picture={member.picture}
                        showTooltip
                        size='sm'
                      />
                    ))}
                  </ChakraV3.AvatarGroup>
                </ChakraV3.HStack>
              </ChakraV3.Button>
            ))}
            <ChakraV3.Stack ref={endRef} />
          </ChakraV3.Stack>
        </ScrollArea>
        <ChakraV3.Spacer />
        <ChakraV3.Separator />
        <ChakraV3.HStack pr={4}>
          <ChakraV3.Text fontSize='xs' whiteSpace='nowrap'>
            {participantDocumentVersionsPagination.total}{' '}
            {inflection.inflect('version', participantDocumentVersionsPagination.total)}
          </ChakraV3.Text>
          <ChakraV3.Spacer minW={4} />
          <ChakraV3.Button
            onClick={fetchNextPage}
            variant='plain'
            fontSize='xs'
            disabled={!hasNextPage}
            loading={isLoading}
            justifyContent='end'
          >
            Load more
          </ChakraV3.Button>
        </ChakraV3.HStack>
      </>
    );
  });

  return (
    <ChakraV3.Stack
      h='full'
      w='320px'
      gap={4}
      flexShrink={0}
      borderRightWidth='1px'
      borderRightColor='gray.200'
    >
      {content}
    </ChakraV3.Stack>
  );
};

interface Props {
  document: ParticipantDocument;
}

export const ParticipantDocumentHistoryModal = createModal<Props>((props) => {
  const { document, modal } = props;

  const { hide, visible } = modal;
  const { organization, successToast, errorToast, location } = useAppContext();

  const [activeVersionId, setActiveVersionId] = React.useState<string | null>(null);

  const restoreParticipantDocumentVersionMutation = useRestoreParticipantDocumentVersionMutation();

  const activeParticipantDocumentQuery = useParticipantDocumentVersionQuery(
    {
      organizationId: organization.id,
      participantId: document.participantId,
      participantDocumentId: document.id,
      versionId: activeVersionId ?? '',
    },
    {
      enabled: activeVersionId !== null,
    },
  );

  const activeParticipantDocument =
    activeParticipantDocumentQuery.data?.organization?.participant?.document;

  const variables = activeParticipantDocument?.variables ?? [];
  const extensions = getParticipantDocumentExtensions({ variables });

  const content = React.useMemo(() => {
    const value = activeParticipantDocument?.value ?? undefined;

    if (!value) {
      return undefined;
    }

    const document = createDocFromUint8Array(value);
    const jsonContent = convertYDocToJSONContent(document);
    const processed = postProcessContent(jsonContent, []);
    const htmlContent = generateHTML(processed, extensions);

    return htmlContent;
  }, [activeParticipantDocument?.value, extensions]);

  const { editor } = useEditor(
    {
      extensions,
      isEditable: false,
      content,
    },
    [activeParticipantDocument],
  );

  React.useEffect(() => {
    if (editor && !editor.isDestroyed && content && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  const onRestoreVersion = React.useCallback(async () => {
    await restoreParticipantDocumentVersionMutation
      .mutateAsync({
        organizationId: organization.id,
        participantDocumentId: document.id,
        participantId: document.participantId,
        versionId: activeVersionId ?? '',
      })
      .then(() => {
        hide();
        successToast('Version restored successfully');
        location.reload();
      })
      .catch((err) => {
        errorToast(`Error restoring version: ${err.message}`);
      });
  }, [
    activeVersionId,
    document.id,
    document.participantId,
    organization.id,
    restoreParticipantDocumentVersionMutation,
    location,
    errorToast,
    hide,
    successToast,
  ]);

  const state: HocuspocusState = P.run(() => {
    if (activeParticipantDocumentQuery.isFetching) {
      return { status: 'connecting' };
    }

    if (activeParticipantDocumentQuery.isError) {
      return { status: 'disconnected' };
    }

    return { status: 'connected', save: { saveStatus: 'saved', saveCount: 0, lastSavedAt: null } };
  });

  return (
    <ChakraV3.Dialog.Root
      open={visible}
      onOpenChange={hide}
      onExitComplete={modal.remove}
      size='cover'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle
            title={document.name}
            subtitle='You can view the history of this document here.'
          />
          <ChakraV3.Dialog.Body>
            <ChakraV3.HStack w='full' h='full' gap={4}>
              <ParticipantDocumentHistorySidebar
                participantId={document.participantId}
                participantDocumentId={document.id}
                activeVersionId={activeVersionId}
                setActiveVersionId={setActiveVersionId}
              />
              <ChakraV3.Stack w='full' h='full' align='center'>
                <ChakraV3.Stack h='full' w='full' overflowY='auto' layerStyle='fade'>
                  {activeParticipantDocument && (
                    <EditorLayout
                      documentId={document.id}
                      isSidebarOpen={true}
                      state={state}
                      headerContent={
                        <ChakraV3.Heading size='3xl' fontWeight='bold' lineClamp={1}>
                          {activeParticipantDocument.name}
                        </ChakraV3.Heading>
                      }
                      toolbarContent={null}
                      mainContent={<Editor editor={editor} />}
                      sidebarContent={<EditorOutlineSection editor={editor} />}
                    />
                  )}
                </ChakraV3.Stack>

                <ChakraV3.AlertBanner
                  status='warning'
                  alignItems='center'
                  fontSize='md'
                  title='Restoring this version will overwrite the current document. Are you sure you
                      want to restore this version?'
                  endElement={
                    <ChakraV3.HStack>
                      <ChakraV3.Button size='sm' onClick={hide} variant='subtle'>
                        Close
                      </ChakraV3.Button>
                      <ChakraV3.Button
                        onClick={onRestoreVersion}
                        colorPalette='brand'
                        size='sm'
                        loading={restoreParticipantDocumentVersionMutation.isLoading}
                      >
                        Restore version
                      </ChakraV3.Button>
                    </ChakraV3.HStack>
                  }
                />
              </ChakraV3.Stack>
            </ChakraV3.HStack>
          </ChakraV3.Dialog.Body>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
